import React, { useState, useEffect } from 'react';
import '../styles/PaymentHistory.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PaymentHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const userData = localStorage.getItem('userAuth');
      if (!userData) {
        setError('Please log in to view payment history');
        setLoading(false);
        return;
      }

      const user = JSON.parse(userData);
      const userId = user.id || user.RowKey; // Support both formats

      const response = await fetch(`${API_URL}/user/billing-history?userId=${userId}`);
      const data = await response.json();

      if (data.success) {
        setTransactions(data.transactions || []);
      } else {
        setError(data.message || 'Failed to load transaction history');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load payment history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: 'status-completed', text: 'Completed' },
      pending: { class: 'status-pending', text: 'Pending' },
      failed: { class: 'status-failed', text: 'Failed' },
      refunded: { class: 'status-refunded', text: 'Refunded' }
    };

    const config = statusConfig[status] || { class: 'status-unknown', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const downloadInvoice = async (transactionId) => {
    try {
      // Placeholder for invoice download functionality
      // Will implement when invoice generation is ready
      alert(`Invoice download for transaction ${transactionId} - Feature coming soon!`);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      alert('Failed to download invoice');
    }
  };

  if (loading) {
    return (
      <div className="payment-history-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-history-container">
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-history-container">
      <div className="payment-history-header">
        <h1>Payment History</h1>
        <p className="subtitle">View all your transactions and invoices</p>
      </div>

      {transactions.length === 0 ? (
        <div className="no-transactions">
          <i className="fas fa-receipt"></i>
          <h3>No Transactions Yet</h3>
          <p>Your payment history will appear here once you subscribe to a package.</p>
        </div>
      ) : (
        <div className="transactions-section">
          <div className="transactions-summary">
            <div className="summary-card">
              <i className="fas fa-check-circle"></i>
              <div>
                <p className="summary-label">Total Transactions</p>
                <p className="summary-value">{transactions.length}</p>
              </div>
            </div>
            <div className="summary-card">
              <i className="fas fa-rupee-sign"></i>
              <div>
                <p className="summary-label">Total Spent</p>
                <p className="summary-value">
                  {formatAmount(
                    transactions
                      .filter(t => t.status === 'completed')
                      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
                  )}
                </p>
              </div>
            </div>
            <div className="summary-card">
              <i className="fas fa-clock"></i>
              <div>
                <p className="summary-label">Pending Payments</p>
                <p className="summary-value">
                  {transactions.filter(t => t.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Transaction ID</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.RowKey}>
                    <td className="date-cell">{formatDate(transaction.createdAt)}</td>
                    <td className="description-cell">
                      <div className="description-content">
                        <strong>{transaction.description || 'Subscription Payment'}</strong>
                        {transaction.subscriptionId && (
                          <small>Subscription ID: {transaction.subscriptionId.substring(0, 8)}...</small>
                        )}
                      </div>
                    </td>
                    <td className="transaction-id-cell">
                      <code>{transaction.RowKey.substring(0, 12)}...</code>
                    </td>
                    <td className="amount-cell">
                      <strong>{formatAmount(transaction.amount, transaction.currency)}</strong>
                    </td>
                    <td className="payment-method-cell">
                      <span className="payment-method">
                        {transaction.paymentMethod === 'paypal' && <i className="fab fa-paypal"></i>}
                        {transaction.paymentMethod === 'card' && <i className="fas fa-credit-card"></i>}
                        {transaction.paymentMethod === 'pending' ? 'Not Paid' : transaction.paymentMethod}
                      </span>
                    </td>
                    <td className="status-cell">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="actions-cell">
                      {transaction.status === 'completed' && (
                        <button
                          className="btn-download-invoice"
                          onClick={() => downloadInvoice(transaction.RowKey)}
                          title="Download Invoice"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      )}
                      {transaction.status === 'pending' && (
                        <button
                          className="btn-pay-now"
                          onClick={() => window.location.href = '/billing'}
                          title="Complete Payment"
                        >
                          Pay Now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="payment-history-footer">
        <p>
          <i className="fas fa-info-circle"></i>
          Need help with a transaction? Contact our support team at{' '}
          <a href="mailto:support@globalresearch.org">support@globalresearch.org</a>
        </p>
      </div>
    </div>
  );
};

export default PaymentHistory;
